import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowLeft, ChevronRight, Search } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { pickLocale } from "../../lib/format";
import { columnsFor } from "../../lib/navigationTree";

/**
 * Full-height sheet with categories as a drill-down.
 *
 * A drill-down rather than an accordion because the sheet owns the whole
 * screen: expanding six departments and twelve garment types in place pushes
 * OUTLET and Endirim below the fold, and the first build of this did exactly
 * that — it also printed "Yeni gələnlər" twice, once as a nav item and once as
 * a department. One level at a time keeps the top list to the eight entries the
 * navigation actually has.
 *
 * It reads the same navigation tree the desktop bar does: one menu, presented
 * two ways, rather than two menus to keep in sync.
 *
 * 2 addım (tapşırıq 2026-10-05, emporium mobil menyusu kimi): bölmə →
 * əsas kateqoriyalar. Bölmənin TƏK sütunu varsa (Geyim → «Geyimlər») ara
 * ekran atlanır: «Geyim ›» birbaşa «← Geri · GEYİMLƏR · Bütün geyimlər ·
 * kateqoriyalar» ekranını açır, «Geri» isə kökə qayıdır.
 */
export default function MobileMenu({
  open, items, categoryTree, user, locale, onToggleLocale, onClose,
}) {
  const { t } = useI18n();
  const [stack, setStack] = useState([]);          // [] | [item] | [item, column]

  useEffect(() => { if (!open) setStack([]); }, [open]);

  // A sheet that lets the page scroll behind it reads as broken on iOS, where
  // the rubber band exposes the page under the menu.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open) return null;

  const close = () => { setStack([]); onClose(); };
  const label = (node) => pickLocale(node?.title, locale) || "";
  const drillInto = (target) => {
    if (stack.length === 0 && target.columns?.length === 1 && target.columns[0].links?.length) {
      setStack([target, target.columns[0]]);
      return;
    }
    setStack([...stack, target]);
  };
  const goBack = () => {
    if (stack.length === 2 && stack[0].columns?.length === 1) setStack([]);
    else setStack(stack.slice(0, -1));
  };
  const allLabel = (node) => {
    const name = label(node);
    if (!name) return t("nav.view_all", "Hamısına bax");
    return `${t("nav.all_prefix", "Bütün")} ${name.toLocaleLowerCase(locale === "az" ? "az" : "en")}`;
  };

  const level = stack.length;
  const current = stack[stack.length - 1];

  let rows = [];
  if (level === 0) {
    rows = items.map((item) => {
      const columns = columnsFor(item, categoryTree);
      return {
        key: item.key || item.to,
        title: item.title,
        to: item.to,
        badge: item.badge,
        accent: item.accent,
        drill: columns.length ? { ...item, columns } : null,
      };
    });
  } else if (level === 1) {
    rows = (current.columns || []).map((column) => ({
      key: column.key,
      title: column.title,
      to: column.to,
      drill: column.links?.length ? column : null,
    }));
  } else {
    rows = (current.links || []).map((child) => ({
      key: child.key, title: child.title, to: child.to, drill: null,
    }));
  }

  const openSearch = () => {
    close();
    window.dispatchEvent(new CustomEvent("atelier:open-search"));
  };

  return (
    <div className="adf-mobile-sheet" id="adf-mobile-sheet" data-testid="mobile-menu">
      <div className="adf-mobile-sheet__scroll">
        <div className="adf-container">
          <div className="adf-mobile-level" data-level={level} key={level}>
            {level > 0 && (
              <button
                type="button"
                className="adf-mobile-back"
                onClick={goBack}
                data-testid="mobile-back"
              >
                <ArrowLeft size={15} strokeWidth={1.5} aria-hidden="true" />
                {t("nav.back", "Geri")}
              </button>
            )}

            {level > 0 && label(current) && (
              <p className="adf-mobile-title" data-testid="mobile-level-title">{label(current)}</p>
            )}

            {level > 0 && current.to && (
              /* The section itself stays reachable — drilling in should not hide
                 "everything in Üst geyim". */
              <NavLink to={current.to} onClick={close} className="adf-mobile-row adf-mobile-row--sub adf-mobile-row--everything">
                {allLabel(current)}
              </NavLink>
            )}

            {rows.map((row) => (row.drill ? (
              <button
                key={row.key}
                type="button"
                className={`adf-mobile-row${level ? " adf-mobile-row--sub" : ""}`}
                onClick={() => drillInto(row.drill)}
                aria-label={`${pickLocale(row.title, locale)} — ${t("nav.open", "Aç")}`}
              >
                <span data-accent={row.accent || undefined}>
                  {pickLocale(row.title, locale)}
                  {pickLocale(row.badge, locale) && (
                    <span className="adf-nav-badge">{pickLocale(row.badge, locale)}</span>
                  )}
                </span>
                <ChevronRight size={16} strokeWidth={1.5} aria-hidden="true" />
              </button>
            ) : (
              <NavLink
                key={row.key}
                to={row.to}
                onClick={close}
                className={`adf-mobile-row${level ? " adf-mobile-row--sub" : ""}`}
              >
                <span data-accent={row.accent || undefined}>
                  {pickLocale(row.title, locale)}
                  {pickLocale(row.badge, locale) && (
                    <span className="adf-nav-badge">{pickLocale(row.badge, locale)}</span>
                  )}
                </span>
              </NavLink>
            )))}

            {level === 0 && (
              <>
                <button type="button" className="adf-mobile-row adf-mobile-row--utility" onClick={openSearch}>
                  <span>{t("nav.search", "Axtarış")}</span>
                  <Search size={16} strokeWidth={1.5} aria-hidden="true" />
                </button>

                <div className="adf-mobile-foot">
                  <Link to={user ? "/account" : "/auth/login"} onClick={close}>
                    {user ? t("nav.account", "Hesabım") : t("nav.login", "Daxil ol")}
                  </Link>
                  <Link to={user ? "/account?tab=wardrobe" : "/auth/login?next=/account%3Ftab%3Dwardrobe"} onClick={close}>
                    {t("nav.wardrobe", "Qarderob")}
                  </Link>
                  <Link to="/gallery" onClick={close}>{t("gallery.title", "Qalereya")}</Link>
                  <Link to="/contact" onClick={close}>
                    {t("nav.concierge", "Concierge")}
                  </Link>
                  <Link to="/contact" onClick={close}>
                    {t("nav.contact", "Əlaqə")}
                  </Link>
                  <button type="button" onClick={onToggleLocale}>
                    {locale === "az" ? "English" : "Azərbaycanca"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
